import { useTranslation } from 'react-i18next';
import type { Currency } from '@hero/schema';
import { useCurrentUserCashbackApi } from '@hero/api-hooks';
import Spinner from '../../../app/Spinner/Spinner';
import { PayButton } from '../../../app/PayButton';
import { Price } from '../../../app/Price';
import { CashbackBanner } from '../../Payment/Cashback';

export interface BalanceCardProps {
  /** Current wallet balance amount */
  balance?: number;
  /** Currency code (e.g. 'SAR', 'USD') */
  currency?: Currency;
  /** Whether balance is loading */
  isLoading: boolean;
  /** Optional action button label (e.g. "Pay Now") */
  actionLabel?: string;
  /** Optional action button click handler */
  onActionClick?: () => void;
  /** Called when user clicks the CashbackBanner action (opens Add Funds drawer) */
  onAddFunds?: () => void;
  /** Image src for the balance indicator (e.g. "/images/invoice-green.svg") */
  imageSrc?: string;
  /**
   * Show the cashback banner when eligible.
   * Set to true for customer context (they owe money),
   * false for carrier context (we owe them). Defaults to false.
   */
  showPayEarlyBanner?: boolean;
}

/**
 * Cashback banners — only mounted in customer context.
 * Calls useCurrentUserCashbackApi (customer-only hook) safely because
 * it is only rendered when showPayEarlyBanner is true.
 */
function CashbackBanners({
  onActionClick,
  onAddFunds,
}: {
  onActionClick?: () => void;
  onAddFunds?: () => void;
}) {
  const { data: cashbackData } = useCurrentUserCashbackApi();

  const advanceCashback = cashbackData?.advancePaymentCashback;
  const invoiceCashback = cashbackData?.invoiceCashback;

  const showEarlyPayBanner = invoiceCashback && invoiceCashback.activeCount > 0 && onActionClick;
  const showAdvanceDepositBanner = advanceCashback?.isEligible && onAddFunds;

  if (!showEarlyPayBanner && !showAdvanceDepositBanner) return null;

  return (
    <>
      {showEarlyPayBanner && (
        <div className="w-full">
          <CashbackBanner
            type="early_pay"
            percentage={invoiceCashback.totalSavings > 0 ? invoiceCashback.totalSavings : 0}
            onActionClick={onActionClick}
          />
        </div>
      )}
      {showAdvanceDepositBanner && (
        <div className="w-full">
          <CashbackBanner
            type="advance_deposit"
            percentage={advanceCashback.percentage}
            onActionClick={onAddFunds}
          />
        </div>
      )}
    </>
  );
}

export function BalanceCard({
  balance = 0,
  currency,
  isLoading,
  actionLabel,
  onActionClick,
  onAddFunds,
  imageSrc = '/images/invoice-green.svg',
  showPayEarlyBanner = false,
}: BalanceCardProps) {
  const { t } = useTranslation(['m-payment']);

  return (
    <div className="flex flex-col items-center justify-center w-full bg-white border rounded-lg py-10">
      <img src={imageSrc} alt="balance" />
      <div className="flex flex-col items-center gap-2 mt-2">
        {isLoading ? (
          <Spinner className="mx-auto" />
        ) : (
          <div className="flex flex-col items-center">
            <h2 className="text-black h3">
              <Price amount={balance} currency={currency} />
            </h2>
            <h5 className="text-gray-500 body2">{t('m-payment:wallet.available_balance')}</h5>
          </div>
        )}
      </div>

      {!isLoading && (
        <div className="mt-6 w-full px-6 flex flex-col items-center gap-3 transition-opacity duration-300">
          {actionLabel && onActionClick && (
            <div className="min-w-[278px]">
              <PayButton label={actionLabel} size="lg" fullWidth onClick={onActionClick} />
            </div>
          )}
          {showPayEarlyBanner && (
            <CashbackBanners onActionClick={onActionClick} onAddFunds={onAddFunds} />
          )}
        </div>
      )}
    </div>
  );
}
